import '../OrderSummaryCard.scss';
import Subtitle from 'components/TextComps/Subtitle';
import { ReactComponent as ShoppingCart } from 'images/icons/ShoppingCart.svg';

import { useTranslation } from 'react-i18next';
import { useCartStore } from 'stores/Webshop/CartStore';
import { CartItem } from 'types/cart';
import { Chip } from '@trackman/web-shared-components';
import { useIsUserMember } from 'queryHooks/permissions';

export const OrderSummaryCardStatic = () => {
  const { t } = useTranslation();

  const { data: isPremium, isPending } = useIsUserMember();

  const itemsInCart: CartItem[] = useCartStore((state) => state.items);
  const tax = useCartStore((state) => state.tax);
  const total = useCartStore((state) => state.total);

  const certificationsInCart = itemsInCart.filter((item) => item.id !== 'premium');
  const hasDiscount = (!isPending && isPremium) || itemsInCart.some((item) => item.id === 'premium');

  return (
    <div className='order-summary-card'>
      {!isPending && itemsInCart.length > 0 ? (
        <>
          <Subtitle>
            <>{t('OrderSummary', { ns: 'webshop' })}</>
          </Subtitle>
          <div className='items-list'>
            {itemsInCart.find((item) => item.id === 'premium') && (
              <SubscriptionSummaryEntry key='premium' subscription={itemsInCart.find((item) => item.id === 'premium')!} />
            )}
            {certificationsInCart.map((item) => (
              <CertificationSummaryEntry key={item.id} certification={item} hasDiscount={hasDiscount} />
            ))}
          </div>
          <div className='summary-totals'>
            <div className='summary-row'>
              <span className='title'>{t('VAT', { ns: 'webshop' })}</span>
              <span className='price'>${tax.toFixed(2)}</span>
            </div>
            <div className='summary-row total-row'>
              <span className='title'>{t('TodaysTotal', { ns: 'webshop' })}</span>
              <span className='price'>${total.toFixed(2)}</span>
            </div>
          </div>
        </>
      ) : (
        <span className='empty-cart'>
          <ShoppingCart /> {t('YourCartIsEmpty', { ns: 'webshop' })}
        </span>
      )}
    </div>
  );
};

const SubscriptionSummaryEntry = ({ subscription }: { subscription: CartItem }) => {
  const { t } = useTranslation();
  const activeStep = useCartStore((state) => state.activeStep);
  const orderCompletionDate = useCartStore((state) => state.orderCompletionDate);
  const date = new Date(orderCompletionDate!);
  date.setFullYear(date.getFullYear() + 1);
  const formattedOrderDate = date.toLocaleDateString('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
  });
  const autoRenewal = useCartStore((state) => state.autoRenew);

  return (
    <div className='summary-row'>
      <div className='title title__subscription'>
        <span>
          {subscription.title}{' '}
          {activeStep === 2 && autoRenewal && (
            <Chip label={t('AutoRenews', { ns: 'webshop', date: formattedOrderDate })} className='auto-renew-badge' />
          )}
        </span>
        <span>/ {t('Annually', { ns: 'webshop' })}</span>
      </div>
      <span className='price'>${subscription.price}</span>
    </div>
  );
};

const CertificationSummaryEntry = ({ certification, hasDiscount }: { certification: CartItem; hasDiscount: boolean }) => {
  const { t } = useTranslation();

  return (
    <div className='summary-row'>
      <span className='title title__certification'>{t('Certification', { ns: 'webshop', certificationName: certification.title })}</span>
      <span className='price'>
        <span className={hasDiscount ? 'base-price base-price_discounted' : 'base-price'}>${certification.price}</span>
        {hasDiscount && <span className='final-price'>${certification.price * 0.5}</span>}
      </span>
    </div>
  );
};
